import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { buildThemeClasses } from "../theme";
import { LEAD_PRODUCTS } from "../services/leadsService";
import { applyOwnerAssignment, assignableAgents, type Agent, type OwnerApplyResult } from "../services/agentsService";
import { getAvailability, saveOwnerAssignment, type OwnerAssignment } from "../services/bookingsService";
import { canManageGlobalIntegrations, fetchIntegrationPermissions } from "../services/integrationsService";

// ===========================================================================
// Responsable por producto. Una sola regla para todo el CRM: quién atiende lo
// de cada producto. La usan las citas del agendador público y los leads y
// negocios NUEVOS; lo que ya tiene responsable no se toca. El botón «Aplicar a
// lo que no tiene responsable» reparte lo que ya existía, con vista previa.
// ===========================================================================

const DEFAULT_KEY = "__default__";
const EMPTY: OwnerAssignment = { byProduct: {}, defaultAgentId: null };

type ApiError = { response?: { data?: { message?: string } }; message?: string };
const errorText = (e: unknown, fallback: string) => {
  const err = e as ApiError;
  return err.response?.data?.message || err.message || fallback;
};

const countLine = (label: string, c: OwnerApplyResult["leads"]) =>
  `<b>${label}:</b> ${c.assigned} de ${c.unassigned} sin responsable` +
  (c.skipped ? ` (${c.skipped} se quedan igual: su producto no tiene responsable)` : "");

export function OwnerByProductCard({ agents }: { agents: Agent[] }) {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const people = useMemo(() => assignableAgents(agents), [agents]);

  const [saved, setSaved] = useState<OwnerAssignment>(EMPTY);
  const [draft, setDraft] = useState<OwnerAssignment>(EMPTY);
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([getAvailability().catch(() => null), fetchIntegrationPermissions()]).then(([availability, perms]) => {
      if (!alive) return;
      const assignment = { ...EMPTY, ...(availability?.assignment ?? {}) };
      setSaved(assignment);
      setDraft(assignment);
      setCanEdit(canManageGlobalIntegrations(perms));
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const valueOf = (key: string) => (key === DEFAULT_KEY ? draft.defaultAgentId : draft.byProduct[key]) || "";
  const setValue = (key: string, agentId: string) =>
    setDraft((prev) =>
      key === DEFAULT_KEY
        ? { ...prev, defaultAgentId: agentId || null }
        : { ...prev, byProduct: { ...prev.byProduct, [key]: agentId } },
    );

  const dirty =
    (draft.defaultAgentId || null) !== (saved.defaultAgentId || null) ||
    LEAD_PRODUCTS.some((p) => (draft.byProduct[p] || "") !== (saved.byProduct[p] || ""));

  const save = async () => {
    setBusy(true);
    try {
      // Solo lo que cambió ("" → null quita al agente): el servidor valida a cada
      // agente que recibe, y uno guardado que ya está inactivo no debe impedir
      // cambiar a los demás.
      const changed = LEAD_PRODUCTS.filter((p) => (draft.byProduct[p] || "") !== (saved.byProduct[p] || ""));
      const next = await saveOwnerAssignment({
        byProduct: Object.fromEntries(changed.map((p) => [p, draft.byProduct[p] || null])),
        ...((draft.defaultAgentId || null) !== (saved.defaultAgentId || null)
          ? { defaultAgentId: draft.defaultAgentId || null }
          : {}),
      });
      const assignment = { ...EMPTY, ...(next ?? {}) };
      setSaved(assignment);
      setDraft(assignment);
      Swal.fire({ icon: "success", title: "Responsables guardados", timer: 1600, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: errorText(e, "No se pudo guardar"), confirmButtonColor: brand.primary });
    } finally {
      setBusy(false);
    }
  };

  const applyToExisting = async () => {
    setBusy(true);
    try {
      const preview = await applyOwnerAssignment(true);
      const total = preview.leads.assigned + preview.deals.assigned;
      if (!total) {
        await Swal.fire({
          icon: "info",
          title: "Nada por repartir",
          text: "Todos los leads y negocios ya tienen responsable, o su producto no tiene uno asignado.",
          confirmButtonColor: brand.primary,
        });
        return;
      }
      const res = await Swal.fire({
        icon: "question",
        title: "Aplicar a lo que no tiene responsable",
        html: `${countLine("Leads", preview.leads)}<br/>${countLine("Negocios", preview.deals)}<br/><br/>Lo que ya tiene responsable no cambia.`,
        showCancelButton: true,
        confirmButtonText: `Asignar ${total}`,
        cancelButtonText: "Cancelar",
        confirmButtonColor: brand.primary,
      });
      if (!res.isConfirmed) return;
      const done = await applyOwnerAssignment(false);
      Swal.fire({
        icon: "success",
        title: "Responsables asignados",
        html: `${countLine("Leads", done.leads)}<br/>${countLine("Negocios", done.deals)}`,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: errorText(e, "No se pudo aplicar el reparto"), confirmButtonColor: brand.primary });
    } finally {
      setBusy(false);
    }
  };

  const control = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;
  const rows = [...LEAD_PRODUCTS.map((p) => ({ key: p as string, label: p as string })), { key: DEFAULT_KEY, label: "Cualquier otro producto" }];

  return (
    <section data-testid="owner-by-product" className={`mb-6 rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={`text-base font-bold ${tc.text}`}>
            <i className="ri-user-shared-line mr-2 text-primary-500" />
            Responsable por producto
          </h2>
          <p className={`mt-1 max-w-3xl text-sm ${tc.textSecondary}`}>
            A quién se le asignan las citas, los leads y los negocios nuevos de cada producto. Lo que ya tiene
            responsable no cambia. Si esa persona es propietaria en HubSpot, también llega allá a su nombre.
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={applyToExisting}
              disabled={busy || dirty}
              title={dirty ? "Guarda los cambios antes de aplicar" : undefined}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 disabled:opacity-50 ${tc.border} ${tc.text}`}
            >
              Aplicar a lo que no tiene responsable
            </button>
            <button
              onClick={save}
              disabled={busy || !dirty}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400 disabled:opacity-50"
            >
              Guardar
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((row) => (
          <label key={row.key} className="block">
            <span className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{row.label}</span>
            <select
              aria-label={`Responsable de ${row.label}`}
              value={valueOf(row.key)}
              disabled={!loaded || !canEdit || busy}
              onChange={(e) => setValue(row.key, e.target.value)}
              className={control}
            >
              <option value="">{row.key === DEFAULT_KEY ? "Nadie" : "Sin responsable"}</option>
              {/* Un agente guardado que dejó de estar activo: se ve, pero ya no reparte. */}
              {valueOf(row.key) && !people.some((a) => a.id === valueOf(row.key)) && (
                <option value={valueOf(row.key)}>
                  {(agents.find((a) => a.id === valueOf(row.key))?.name ?? "Agente no disponible") + " (inactivo)"}
                </option>
              )}
              {people.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      {loaded && !canEdit && (
        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-lock-line mr-1" />
          Solo un administrador de la plataforma puede cambiar este reparto.
        </p>
      )}
    </section>
  );
}
